import { useI18n } from "../lib/i18n";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS } from "../lib/bookingStatus";

export default function Contact() {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();

  const de = lang === "de";

  return (
    <div className="min-h-screen bg-ivory" data-testid="contact-page">
      {/* Navy header */}
      <header className="text-ivory" style={{ background: BRAND_COLORS.navy }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-4">
            <div className="w-11 h-11 border border-gold/50 flex items-center justify-center">
              <span className="font-display text-ivory text-[26px] leading-none pt-[2px]">W</span>
            </div>
            <div className="font-display text-ivory tracking-[0.28em] text-[18px] pt-[2px]">WEISSBLOM</div>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex text-[11px] tracking-[0.2em] uppercase text-ivory/60">
              <button data-testid="contact-lang-de" onClick={() => setLang("de")} className={lang === "de" ? "text-gold" : ""}>DE</button>
              <span className="mx-2 opacity-40">/</span>
              <button data-testid="contact-lang-en" onClick={() => setLang("en")} className={lang === "en" ? "text-gold" : ""}>EN</button>
            </div>
            <button
              data-testid="contact-back-btn"
              onClick={() => nav(user ? -1 : "/login")}
              className="text-[11px] tracking-[0.2em] uppercase text-ivory/80 hover:text-ivory border-b border-gold pb-0.5"
            >
              ← {de ? "Zurück" : "Back"}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-14 md:py-20 page-fade">
        <div className="max-w-2xl mb-16">
          <div className="label-caps mb-3">{de ? "Kontakt" : "Contact"}</div>
          <h1 className="font-display text-5xl md:text-6xl text-navy leading-[1.05]">
            {de ? "Sprechen Sie mit uns" : "Get in touch"}
          </h1>
          <p className="text-[15px] text-navy-dark/70 mt-4 leading-relaxed">
            {de
              ? "Ihr direkter Draht zum Weissblom-Team. Wir sind für Sie da — vor, während und nach Ihrer Abwesenheit."
              : "Your direct line to the Weissblom team. We are here for you — before, during and after your absence."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <a
            href="mailto:info@weissblom.de"
            data-testid="contact-email-card"
            className="bg-white border border-navy/10 p-8 hover:border-gold transition-colors block"
          >
            <div className="label-caps mb-3">{de ? "E-Mail" : "Email"}</div>
            <div className="font-display text-2xl text-navy mb-2">team@concierge.<br/>weissblom.de</div>
            <div className="text-[12px] text-navy-dark/60 mt-4 border-t border-navy/10 pt-3">
              {de ? "Antwort innerhalb von 24 Stunden werktags." : "Reply within 24 h on weekdays."}
            </div>
          </a>
          <div className="bg-white border border-navy/10 p-8" data-testid="contact-hours-card">
            <div className="label-caps mb-3">{de ? "Erreichbarkeit" : "Availability"}</div>
            <div className="font-display text-2xl text-navy mb-2">{de ? "Mo – Fr" : "Mon – Fri"}</div>
            <div className="text-[13px] text-navy-dark/70">09:00 – 18:00 CET</div>
            <div className="text-[12px] text-navy-dark/60 mt-4 border-t border-navy/10 pt-3">
              {de ? "Notfälle & Abwesenheiten: 24/7 über Ihr Concierge-Team." : "Emergencies & absences: 24/7 via your concierge team."}
            </div>
          </div>
          <div className="bg-navy p-8 text-ivory" style={{ background: BRAND_COLORS.navy }} data-testid="contact-office-card">
            <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>{de ? "Büro" : "Office"}</div>
            <div className="font-display text-2xl text-ivory mb-1">Weissblom</div>
            <div className="text-[13px] text-ivory/80 leading-relaxed">
              Agentur für Immobilienbetreuung<br/>
              Maximiliansplatz 12<br/>
              80333 München<br/>
              Deutschland
            </div>
          </div>
        </div>

        {/* Impressum */}
        <section className="bg-white border border-navy/10 p-8 md:p-12 mb-8" data-testid="impressum-section">
          <div className="flex items-baseline gap-4 mb-8">
            <h2 className="font-display text-2xl md:text-3xl text-navy">{de ? "Impressum" : "Legal notice"}</h2>
            <div className="flex-1 border-b border-gold/40" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12 text-[14px] text-navy-dark leading-relaxed">
            <div>
              <div className="label-caps mb-2">{de ? "Anbieter" : "Provider"}</div>
              <div className="text-navy font-medium">Weissblom GmbH</div>
              <div>Agentur für Immobilienbetreuung</div>
              <div>Maximiliansplatz 12</div>
              <div>80333 München, Deutschland</div>
            </div>
            <div>
              <div className="label-caps mb-2">{de ? "Vertretungsberechtigt" : "Represented by"}</div>
              <div>Ramkeer Smaekers, Geschäftsführer</div>
              <div className="label-caps mt-4 mb-2">{de ? "Registereintrag" : "Register entry"}</div>
              <div>Handelsregister München · HRB 289 471</div>
              <div>USt-IdNr.: DE 349 812 407</div>
            </div>
            <div>
              <div className="label-caps mb-2">{de ? "Kontakt" : "Contact"}</div>
              <div>E-Mail: <a href="mailto:info@weissblom.de" className="text-navy border-b border-gold">info@weissblom.de</a></div>
              <div>Telefon: <a href="tel:+4915566200285" className="text-navy border-b border-gold">+49 155 662 002 85</a></div>
            </div>
            <div>
              <div className="label-caps mb-2">{de ? "Aufsichtsbehörde" : "Supervisory authority"}</div>
              <div>Kreisverwaltungsreferat München</div>
              <div>Ruppertstraße 19, 80337 München</div>
            </div>
          </div>
        </section>

        {/* Datenschutz kurz */}
        <section className="bg-white border border-navy/10 p-8 md:p-12 mb-8" data-testid="privacy-section">
          <div className="flex items-baseline gap-4 mb-6">
            <h2 className="font-display text-2xl md:text-3xl text-navy">{de ? "Datenschutz" : "Privacy"}</h2>
            <div className="flex-1 border-b border-gold/40" />
          </div>
          <div className="text-[14px] text-navy-dark/80 leading-relaxed space-y-4 max-w-3xl">
            <p>
              {de
                ? "Wir verarbeiten Ihre personenbezogenen Daten ausschließlich zur Erbringung unserer Betreuungsdienstleistungen. Zugriff auf Objektdaten, Fotos und Nachrichten haben nur das Weissblom-Team und die von Ihnen autorisierten Familienmitglieder."
                : "We process your personal data solely to deliver our concierge services. Only the Weissblom team and family members you authorise have access to property data, photos and messages."}
            </p>
            <p>
              {de
                ? "Sie haben jederzeit das Recht auf Auskunft, Berichtigung, Löschung sowie Einschränkung der Verarbeitung Ihrer Daten. Wenden Sie sich hierzu bitte an info@weissblom.de."
                : "You have the right to information, correction, deletion and restriction of your data processing at any time. Please contact info@weissblom.de."}
            </p>
            <p className="text-[12px] text-navy-dark/60 italic">
              {de
                ? "Die vollständige Datenschutzerklärung erhalten Sie auf Anfrage."
                : "The complete privacy statement is available on request."}
            </p>
          </div>
        </section>

        {/* Haftungshinweis */}
        <section className="bg-white border border-navy/10 p-8 md:p-12" data-testid="liability-section">
          <div className="flex items-baseline gap-4 mb-6">
            <h2 className="font-display text-2xl md:text-3xl text-navy">{de ? "Haftungshinweis" : "Liability"}</h2>
            <div className="flex-1 border-b border-gold/40" />
          </div>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed max-w-3xl">
            {de
              ? "Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich."
              : "Despite careful content control, we accept no liability for the content of external links. Only the operators of the linked pages are responsible for their content."}
          </p>
        </section>
      </div>

      <footer className="border-t border-navy/10 py-8 text-center bg-white">
        <div className="label-caps">© {new Date().getFullYear()} Weissblom · Private Property Concierge</div>
      </footer>
    </div>
  );
}
